import { isRecord, getErrorMessage } from "../../utils/errors";
import { logger } from "../../utils/logger";
import axios, { AxiosError } from "axios";
import type { ApiError, ValidationError, RetryConfig } from "./types";

export class ApiRequestError extends Error implements ApiError {
  code: string;
  status?: number;
  details?: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiRequestError";
    this.code = error.code;
    this.status = error.status;
    this.details = error.details;
  }
}

export class ErrorHandler {
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    retryCondition: (error: ApiError) => {
      // Retry on network errors and 5xx server errors
      return !error.status || (error.status >= 500 && error.status < 600);
    },
  };

  constructor(retryConfig?: Partial<RetryConfig>) {
    if (retryConfig) {
      this.retryConfig = { ...this.retryConfig, ...retryConfig };
    }
  }

  /**
   * Handle and format API errors consistently
   */
  handleError(error: unknown): ApiError {
    if (axios.isAxiosError(error)) {
      return this.handleAxiosError(error);
    }

    if (
      typeof error === "object" &&
      error !== null &&
      "message" in error &&
      "code" in error &&
      typeof error.message === "string" &&
      typeof error.code === "string"
    ) {
      return error instanceof Error
        ? (error as Error & ApiError)
        : new ApiRequestError(error as ApiError);
    }

    if (error instanceof Error) {
      return new ApiRequestError({
        message: error.message,
        code: "UNKNOWN_ERROR",
      });
    }

    return new ApiRequestError({
      message: "An unexpected error occurred",
      code: "UNKNOWN_ERROR",
    });
  }

  /**
   * Handle Axios-specific errors
   */
  private handleAxiosError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      const errorData = data;

      return new ApiRequestError({
        message: this.extractErrorMessage(errorData),
        code: this.getErrorCode(status, errorData),
        status,
        details: errorData,
      });
    }

    if (error.request) {
      // Network error - no response received
      return new ApiRequestError({
        message: "Network error. Please check your connection and try again.",
        code: "NETWORK_ERROR",
      });
    }

    // Request setup error
    return new ApiRequestError({
      message: error.message || "Request configuration error",
      code: "REQUEST_ERROR",
    });
  }

  /**
   * Extract user-friendly error message from response data
   */
  private extractErrorMessage(errorData: unknown): string {
    if (isRecord(errorData)) {
      for (const key of ["error", "message", "details"]) {
        if (typeof errorData[key] === "string") return errorData[key];
      }
      if (Array.isArray(errorData.errors)) {
        const errors = errorData.errors.filter(
          (item): item is ValidationError =>
            isRecord(item) &&
            typeof item.field === "string" &&
            typeof item.message === "string",
        );
        if (errors.length) return this.formatValidationErrors(errors);
      }
    }
    return getErrorMessage(
      errorData,
      "An error occurred while processing your request",
    );
  }

  /**
   * Generate appropriate error code based on status and response data
   */
  private getErrorCode(status: number, errorData: unknown): string {
    // Use server-provided error code if available
    if (isRecord(errorData) && typeof errorData.code === "string") {
      return errorData.code;
    }

    // Generate code based on HTTP status
    switch (status) {
      case 400:
        return "BAD_REQUEST";
      case 401:
        return "UNAUTHORIZED";
      case 403:
        return "FORBIDDEN";
      case 404:
        return "NOT_FOUND";
      case 409:
        return "CONFLICT";
      case 422:
        return "VALIDATION_ERROR";
      case 429:
        return "RATE_LIMITED";
      case 500:
        return "INTERNAL_SERVER_ERROR";
      case 502:
        return "BAD_GATEWAY";
      case 503:
        return "SERVICE_UNAVAILABLE";
      case 504:
        return "GATEWAY_TIMEOUT";
      default:
        return `HTTP_${status}`;
    }
  }

  /**
   * Format validation errors into a readable message
   */
  formatValidationErrors(errors: ValidationError[]): string {
    if (errors.length === 1) {
      return `${errors[0].field}: ${errors[0].message}`;
    }

    const errorMessages = errors.map((err) => `${err.field}: ${err.message}`);
    return `Validation errors:\n${errorMessages.join("\n")}`;
  }

  /**
   * Determine if an error should be retried
   */
  shouldRetry(error: ApiError, attemptCount: number): boolean {
    if (attemptCount >= this.retryConfig.maxRetries) {
      return false;
    }

    return this.retryConfig.retryCondition(error);
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  getRetryDelay(attemptCount: number): number {
    const delay = this.retryConfig.baseDelay * Math.pow(2, attemptCount - 1);
    return Math.min(delay, this.retryConfig.maxDelay);
  }

  /**
   * Log error for debugging (can be extended to use proper logging service)
   */
  logError(error: ApiError, context?: string): void {
    const logData = {
      error,
      context,
      timestamp: new Date().toISOString(),
    };

    if (import.meta.env?.DEV) {
      logger.error("API Error:", logData);
    }

    // In production, you might want to send this to a logging service
    // Example: LoggingService.error(logData);
  }

  /**
   * Get user-friendly error message for display
   */
  getUserFriendlyMessage(error: ApiError): string {
    switch (error.code) {
      case "NETWORK_ERROR":
        return "Unable to connect to the server. Please check your internet connection.";
      case "UNAUTHORIZED":
        return "Your session has expired. Please log in again.";
      case "FORBIDDEN":
        return "You do not have permission to perform this action.";
      case "NOT_FOUND":
        return "The requested resource was not found.";
      case "VALIDATION_ERROR":
        return error.message; // Validation messages are already user-friendly
      case "RATE_LIMITED":
        return "Too many requests. Please wait a moment and try again.";
      case "SERVICE_UNAVAILABLE":
        return "The service is temporarily unavailable. Please try again later.";
      default:
        return (
          error.message || "An unexpected error occurred. Please try again."
        );
    }
  }
}
